import { rem } from 'polished'
import type { CSSProperties, MouseEvent } from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'

import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import CalendarEventOverlayIcon from './CalendarEventOverlayIcon'
import { CALENDAR_EVENT_OVERLAY_FILL } from './calendarEventOverlay'
import { useCalendarEventTooltip } from './CalendarEventTooltipBubble'

/**
 * Hint beside a paging chevron that the month it would reveal holds an event.
 *
 * Indicative only — the chevron beside it is the control — so this is not
 * focusable. The event name is shown on hover and carried as the label, so
 * assistive tech does not depend on hovering.
 */
const PreviewChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${CALENDAR_EVENT_OVERLAY_FILL};
  line-height: 0;
`

interface Props {
  event: Pick<SearchCalendarEvent, 'id' | 'icon' | 'color'>
  /** Event name shown on hover and announced as the chip's label. */
  label: string
  /** Fires once per pointer entry onto the chip. */
  onHover?: (eventId: string) => void
  className?: string
}

function CalendarEventMonthPreview(props: Props) {
  const { event, label, onHover, className } = props
  const { bubble, hide, show } = useCalendarEventTooltip(
    label,
    `calendar-event-month-preview-tooltip-${event.id}`,
  )

  const onMouseEnter = useCallback((mouseEvent: MouseEvent<HTMLSpanElement>) => {
    show(mouseEvent.currentTarget)
    onHover?.(event.id)
  }, [event.id, onHover, show])

  return (
    <>
      <PreviewChip
        className={className}
        style={{ '--event-overlay-color': event.color } as CSSProperties}
        role="img"
        aria-label={label}
        data-testid={`calendar-event-month-preview-${event.id}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={hide}
      >
        <CalendarEventOverlayIcon name={event.icon} size={12} />
      </PreviewChip>
      {bubble}
    </>
  )
}

export default CalendarEventMonthPreview
